import { isPcbProductValid, bestPcbPanel } from "../pcbCosting";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, Stack, TextField, Typography } from "@mui/material";
import QuestionsSection from "../../../../shared/sections/QuestionsSection";
import PerlLabelMedium from "../../../../shared/components/PerlLabelMedium";
import PerlTextField from "../../../../shared/components/PerlTextField";

const BOARD_TYPE_OPTIONS = [
  "DC-SCM",
  "E1.L",
  "E1.S",
  "Fan",
  "FPGA",
  "HDD Backplane",
  "M.2",
  "Mother Board",
  "OAM",
  "PCIe",
  "PDB",
  "Riser",
  "UBB",
];

const BOARD_TYPE_BEST_PARAMS = {
  "DC-SCM": { thickness: 1.5 },
  "E1.L": { thickness: 1.5 },
  "E1.S": { thickness: 1.5 },
  Fan: { thickness: 1 },
  FPGA: { thickness: 1.6 },
  "HDD Backplane": { thickness: 2.5 },
  "M.2": { thickness: 1.6 },
  "Mother Board": { thickness: 2.4 },
  OAM: { thickness: 4 },
  PCIe: { thickness: 1.6 },
  PDB: { thickness: 2 },
  Riser: { thickness: 1.6 },
  UBB: { thickness: 3 },
};

const COUNTRY_OPTIONS = [
  "Albania",
  "Argentina",
  "Australia",
  "Austria",
  "Belgium",
  "Bosnia",
  "Brazil",
  "Bulgaria",
  "Canada",
  "China",
  "Czech Republic",
  "Denmark",
  "Egypt",
  "Finland",
  "France",
  "Germany",
  "Greece",
  "Hong Kong",
  "Hungary",
  "India",
  "Indonesia",
  "Ireland",
  "Italy",
  "Japan",
  "Luxembourg",
  "Malaysia",
  "Mauritius",
  "Mexico",
  "Morocco",
  "Netherlands",
  "New Zealand",
  "Philiphines",
  "Philippines",
  "Poland",
  "Portugal",
  "Romania",
  "Russia",
  "Saudi Arabia",
  "Serbia",
  "Singapore",
  "Slovakia",
  "Slovenia",
  "South Africa",
  "South Korea",
  "Spain",
  "Sri Lanka",
  "Sweden",
  "Switzerland",
  "Taiwan",
  "Thailand",
  "Tunisia",
  "Turkey",
  "UAE",
  "United Kingdom",
  "USA",
  "Vietnam",
];

const PANEL_SIZE_DEFAULT = "24 X 20";
const BOARD_DIMENSION_MIN = 5;
const BOARD_DIMENSION_MAX = 610;
const BATCH_SIZE_MIN = 1;
const BATCH_SIZE_MAX = 10000;

const getBestThickness = (boardType) => {
  const best = BOARD_TYPE_BEST_PARAMS[boardType]?.thickness;
  return best !== undefined && best !== null ? String(best) : "";
};

const getDemoDefaults = () => {
  const boardType = "PCIe";
  return {
    productName: "PCIe Backplane Rev A",
    boardType,
    boardLengthMm: "150",
    boardWidthMm: "120",
    boardThicknessMm: getBestThickness(boardType),
    panelSizeIn: PANEL_SIZE_DEFAULT,
    estimatedBatchSize: "5000",
    manufacturingCountry: "China",
  };
};

const isDimensionWithinLimits = (value) => {
  const numericValue = Number(value);
  if (value === "" || Number.isNaN(numericValue)) return false;
  return numericValue >= BOARD_DIMENSION_MIN && numericValue <= BOARD_DIMENSION_MAX;
};

const dimensionError = (value) => {
  if (value === "") return "";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "Enter a valid number.";
  if (numericValue < BOARD_DIMENSION_MIN) return `Min ${BOARD_DIMENSION_MIN} mm.`;
  if (numericValue > BOARD_DIMENSION_MAX) return `Max ${BOARD_DIMENSION_MAX} mm.`;
  return "";
};

const isBatchSizeWithinLimits = (value) => {
  const numericValue = Number(value);
  if (value === "" || Number.isNaN(numericValue)) return false;
  return Number.isInteger(numericValue) && numericValue >= BATCH_SIZE_MIN && numericValue <= BATCH_SIZE_MAX;
};

const batchSizeError = (value) => {
  if (value === "") return "";
  const numericValue = Number(value);
  if (!Number.isInteger(numericValue)) return "Enter a whole number.";
  if (numericValue < BATCH_SIZE_MIN || numericValue > BATCH_SIZE_MAX) {
    return `Batch size must be ${BATCH_SIZE_MIN} - ${BATCH_SIZE_MAX}.`;
  }
  return "";
};

const buildInitialData = (initialData) => ({
  productName: "",
  boardType: "",
  boardLengthMm: "",
  boardWidthMm: "",
  boardThicknessMm: "",
  panelSizeIn: PANEL_SIZE_DEFAULT,
  estimatedBatchSize: "",
  manufacturingCountry: "",
  ...initialData,
});

const getInitialStep = (data) => {
  if (!data) return 0;
  if (data.manufacturingCountry) return 4;
  if (data.estimatedBatchSize) return 3;
  if (data.boardLengthMm && data.boardWidthMm) return 2;
  if (data.boardType) return 1;
  if (data.productName) return 0;
  return 0;
};

export default function PCBQuestions({ onComplete, onDataChange, initialData }) {
  const [formData, setFormData] = useState(buildInitialData(initialData));
  const [currentStep, setCurrentStep] = useState(getInitialStep(initialData));
  const [demoMode, setDemoMode] = useState(false);
  const lastSent = useRef(null);
  const emittedData = useRef(new WeakSet());

  useEffect(() => {
    // The parent returns our payload unchanged. An older echo can arrive while
    // a newer local edit is pending; neither echo may overwrite that draft.
    // Object identity distinguishes these echoes from genuine external seeds.
    if (!initialData || emittedData.current.has(initialData)) return;
    const next = buildInitialData(initialData);
    const serialized = JSON.stringify(next);
    setFormData((prev) => (JSON.stringify(prev) === serialized ? prev : next));
    // Avoid jumping steps when updates are echoes of what we just sent upstream
    if (serialized !== lastSent.current) {
      setCurrentStep(getInitialStep(next));
    }
  }, [initialData]);

  const boardSizeDisplay =
    formData.boardLengthMm && formData.boardWidthMm
      ? `${formData.boardLengthMm} x ${formData.boardWidthMm}`
      : "";

  const allAnswered = useMemo(() => isPcbProductValid(formData), [formData]);

  useEffect(() => {
    const payload = allAnswered ? {
      ...formData,
      boardThicknessMm: formData.boardThicknessMm || getBestThickness(formData.boardType),
      panelSizeIn: formData.panelSizeIn || PANEL_SIZE_DEFAULT,
    } : null;
    const serialized = JSON.stringify(payload);
    if (serialized !== lastSent.current) {
      lastSent.current = serialized;
      if (payload) emittedData.current.add(payload);
      onDataChange?.(payload);
    }
  }, [allAnswered, formData, onDataChange]);

  const selectedValues = [
    { label: "Project Name", value: formData.productName },
    { label: "Board Type", value: formData.boardType },
    { label: "Board Size (mm)", value: boardSizeDisplay },
    {
      label: "Estimated Batch Size",
      value: formData.estimatedBatchSize
        ? `${Number(formData.estimatedBatchSize).toLocaleString()} units`
        : "",
    },
    { label: "Manufacturing Country", value: formData.manufacturingCountry },
  ];

  const isNextDisabled = (step) => {
    switch (step) {
      case 0:
        return !formData.productName || formData.productName.trim() === "" || formData.productName.trim().length > 50;
      case 1:
        return !formData.boardType;
      case 2:
        return (
          !isDimensionWithinLimits(formData.boardLengthMm) ||
          !isDimensionWithinLimits(formData.boardWidthMm) || !bestPcbPanel(formData)
        );
      case 3:
        return !isBatchSizeWithinLimits(formData.estimatedBatchSize);
      case 4:
        return !formData.manufacturingCountry;
      default:
        return false;
    }
  };

  const handleComplete = () => {
    if (!allAnswered) return;
    const payload = {
      ...formData,
      boardThicknessMm: formData.boardThicknessMm || getBestThickness(formData.boardType),
      panelSizeIn: formData.panelSizeIn || PANEL_SIZE_DEFAULT,
    };
    emittedData.current.add(payload);
    onComplete?.(payload);
  };

  const handleReset = () => {
    setFormData(buildInitialData());
    setCurrentStep(0);
    setDemoMode(false);
    lastSent.current = "null";
    onDataChange?.(null, { reset: true });
  };

  const handleDemoToggle = (checked) => {
    setDemoMode(checked);
    if (checked) {
      setFormData(buildInitialData(getDemoDefaults()));
      setCurrentStep(4);
    } else {
      handleReset();
    }
  };

  return (
    <QuestionsSection
      selectedValues={selectedValues}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      onComplete={handleComplete}
      brandColor="#ff6034"
      isNextDisabled={isNextDisabled}
      onReset={handleReset}
      demoMode={demoMode}
      onDemoToggle={handleDemoToggle}
    >
      {/* Step 1: Project Name */}
      <Box>
        <PerlLabelMedium sx={{ mb: 2, display: "block" }}>
          Enter project name
        </PerlLabelMedium>
        <PerlTextField
          placeholder="e.g., Phoenix Backplane Rev B"
          value={formData.productName}
          onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
        />
        <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
          Used in costing, reporting, and visualizations.
        </Typography>
      </Box>

      {/* Step 2: Board Type */}
      <Box>
        <PerlLabelMedium sx={{ mb: 2, display: "block" }}>
          Select board type
        </PerlLabelMedium>
        <Autocomplete
          value={formData.boardType || ""}
          onChange={(_, value) => {
            const nextThickness = value ? getBestThickness(value) : "";
            setFormData((prev) => ({
              ...prev,
              boardType: value || "",
              boardThicknessMm: nextThickness,
              panelSizeIn: value ? PANEL_SIZE_DEFAULT : prev.panelSizeIn,
            }));
          }}
          options={BOARD_TYPE_OPTIONS}
          renderInput={(params) => <TextField {...params} placeholder="e.g., PCIe, Backplane" />}
          fullWidth
        />
        <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
          Board type sets recommended thickness and panel presets.
        </Typography>
      </Box>

      {/* Step 3: Board Geometry */}
      <Stack spacing={1.5}>
        <PerlLabelMedium>Board geometry (mm)</PerlLabelMedium>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
          <TextField
            label="Board Length (mm)"
            value={formData.boardLengthMm}
            onChange={(e) => setFormData({ ...formData, boardLengthMm: e.target.value })}
            error={Boolean(dimensionError(formData.boardLengthMm))}
            helperText={dimensionError(formData.boardLengthMm) || undefined}
            inputProps={{ inputMode: "decimal", min: BOARD_DIMENSION_MIN, max: BOARD_DIMENSION_MAX }}
            fullWidth
          />
          <TextField
            label="Board Width (mm)"
            value={formData.boardWidthMm}
            onChange={(e) => setFormData({ ...formData, boardWidthMm: e.target.value })}
            error={Boolean(dimensionError(formData.boardWidthMm))}
            helperText={dimensionError(formData.boardWidthMm) || undefined}
            inputProps={{ inputMode: "decimal", min: BOARD_DIMENSION_MIN, max: BOARD_DIMENSION_MAX }}
            fullWidth
          />
        </Box>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Valid range {BOARD_DIMENSION_MIN}-{BOARD_DIMENSION_MAX} mm. Defaults: panel {PANEL_SIZE_DEFAULT}
          and board thickness {getBestThickness(formData.boardType) || "—"} mm based on board type.
        </Typography>
      </Stack>

      {/* Step 4: Estimated Batch Size */}
      <Box>
        <PerlLabelMedium sx={{ mb: 2, display: "block" }}>
          Estimated batch size (units)
        </PerlLabelMedium>
        <PerlTextField
          type="number"
          placeholder="e.g., 5000"
          value={formData.estimatedBatchSize}
          onChange={(e) => setFormData({ ...formData, estimatedBatchSize: e.target.value })}
          error={Boolean(batchSizeError(formData.estimatedBatchSize))}
          helperText={batchSizeError(formData.estimatedBatchSize) || undefined}
          inputProps={{ min: BATCH_SIZE_MIN, max: BATCH_SIZE_MAX }}
        />
        <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
          Volume driver for scaling (range {BATCH_SIZE_MIN} - {BATCH_SIZE_MAX} units).
        </Typography>
      </Box>

      {/* Step 5: Manufacturing Country */}
      <Box>
        <PerlLabelMedium sx={{ mb: 2, display: "block" }}>
          Select manufacturing country
        </PerlLabelMedium>
        <Autocomplete
          value={formData.manufacturingCountry || ""}
          onChange={(_, value) => setFormData({ ...formData, manufacturingCountry: value || "" })}
          options={COUNTRY_OPTIONS}
          renderInput={(params) => <TextField {...params} placeholder="Type to search..." />}
          fullWidth
        />
        <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
          Sets labor and logistics region assumptions.
        </Typography>
      </Box>
    </QuestionsSection>
  );
}
